import { useEditor, useNode, UserComponent } from '@craftjs/core';
import {
  DataBlockDictionaryColumnDto,
  DataBlockDictionaryItemDto,
  DataBlockTableColumnDto,
  DataType,
  QueryFactory,
  useDataBlockType,
} from '@lmt/shared-types';
import { useDictionariesByIds } from '@lmt/unicorn-shared/app/api/custom-hooks';
import {
  DataChange,
  DataGrid,
  DXG,
  useDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { assertNever } from '@lmt/unicorn-shared/app/helpers/assert-never';
import { emptyArray } from '@lmt/unicorn-shared/app/helpers/common';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import { convertLocalDateToUTC } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { NS_DataBlockFields } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useCustomCompareMemo } from '@lmt/unicorn-shared/app/helpers/useCustomCompareMemo';
import { useEventCallback } from '@mui/material';
import type DevDataGrid from 'devextreme/ui/data_grid';
import equal from 'fast-deep-equal';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import { useContextSelector } from 'use-context-selector';
import { StyledAutocomplete } from '../../../common/ui-kit/inputs/dropdown/StyledAutocomplete';
import { RadioButton } from '../../../common/ui-kit/RadioButton';
import { getDataBlockTypeNameForLocalization } from '../../../measurements/common';
import {
  InputValuesContext,
  UIEditorDataObjectValue,
} from '../../UIEditorTypes';
import styles from './UIEditorBlocks.module.scss';
import { UIEditorBlockWidth, WidthSelectControl } from './WidthSelectControl';

type DefaultProps = {
  size: UIEditorBlockWidth;
  isLabel: boolean;
  label: string;
  isEditable: boolean;
  isShared: boolean;
  // Existing table configs may already have isLabel=true, so title rendering stays opt-in.
  isTitleVisible: boolean;
  // Tables do not use BlockContainer, so this opt-in aligns their edges with input fields.
  isAlignedWithInputs: boolean;
};
export type MandatoryProps = {
  dataKey: string;
  dataBlockFieldId: number;
};

type TableProps = DefaultProps & MandatoryProps;
type Row = { __id: string } & Record<string, unknown>;

const initialChangesState = {
  changes: [] as DataChange[],
  editRowKey: undefined as string | undefined,
  editColumnKey: undefined as string | undefined,
};

type PendingFocus = {
  rowKey: string;
  colName: string;
  keepUntilAutoFocus: boolean;
} | null;

function normalizeDxKey(key: any): string {
  const s = String(key ?? '');
  return s.includes('_DX_KEY_') ? s.replace('_DX_KEY_', '') : s;
}

export const Table: UserComponent<MandatoryProps & Partial<DefaultProps>> =
  React.memo(function Table(props: MandatoryProps & Partial<DefaultProps>) {
    useDataGrid();
    const i18nField = useTranslation(NS_DataBlockFields);

    const { connectors } = useNode((state) => ({
      selected: state.events.selected,
      dragged: state.events.dragged,
    }));

    const { connect, drag } = connectors!;

    const {
      size,
      isShared,
      dataKey,
      label: propsLabel,
      isTitleVisible,
      isAlignedWithInputs,
    } = {
      ...TableDefaultProps,
      ...props,
    };

    const fieldQuery = QueryFactory.DataBlockFieldQuery.useGetByIdQuery(
      props.dataBlockFieldId ?? 0,
      {
        enabled: !!props.dataBlockFieldId,
      },
    );

    const inputValueFromContext: Row[] =
      useContextSelector(InputValuesContext, (v): any => {
        const values = isShared ? v.sharedValues : v.values;
        if (values) {
          if (!values[dataKey]) {
            values[dataKey] = [];
          }
          return values[dataKey];
        } else {
          return undefined;
        }
      }) ?? [];

    const mappedValueFromContext: Row[] = useCustomCompareMemo(
      () => {
        return inputValueFromContext.map((v) => {
          const row: Record<string, any> = {};
          if (!fieldQuery.data?.tableColumns) return v;
          for (const column of fieldQuery.data?.tableColumns) {
            if (v[column.name] !== undefined) {
              row[column.name] = v[column.name];
              if (column.dataType === DataType.DateTime && !!v[column.name]) {
                row[column.name] = new Date(v[column.name] as string);
              }
            }
          }
          row['__id'] = v['__id'];
          return row;
        }) as Row[];
      },
      [inputValueFromContext, fieldQuery.data],
      equal,
    );

    const isOnlyView = useContextSelector(
      InputValuesContext,
      (v) => v.isOnlyView,
    );
    const dataBlockTypeName = useContextSelector(
      InputValuesContext,
      (v) => v.dataBlockTypeName,
    );

    const { isInDesigner } = useEditor((state) => ({
      isInDesigner: state.options.enabled,
    }));

    const label =
      propsLabel === ''
        ? i18nField.t(dataBlockTypeName + '.' + dataKey.toLowerCase(), {
            defaultValue: dataKey,
          })
        : propsLabel;

    const dataBlockType = useDataBlockType(fieldQuery.data?.dataBlockTypeId);

    const columnLocalizationPrefix = dataBlockType
      ? getDataBlockTypeNameForLocalization(dataBlockType) +
        '._fields.' +
        fieldQuery.data?.name +
        '_columns'
      : undefined;

    const dataGridRef = useRef<DataGrid<Row, string>>(null);

    const tableKey =
      /* to update when switching between edit/view */
      (isInDesigner || isOnlyView ? '0' : '1') +
      /* to update the table when Columns are fetched from backend */
      (fieldQuery.data ? '0' : '1');
    const idField = '__id';

    // DevExtreme focus bugfix when adding a row
    // See: Pull Request 24523: Bug #62528: The first cell of the first row is temporarily activated when adding a new row for a table
    // We keep pendingFocusRef + editCell() on contentReady to ensure caret lands in the new row cell
    const pendingFocusRef = useRef<PendingFocus>(null);

    const setPending = useCallback(
      (rowKey: string, colName: string, keepUntilAutoFocus: boolean) => {
        pendingFocusRef.current = {
          rowKey: String(rowKey),
          colName,
          keepUntilAutoFocus,
        };
      },
      [],
    );

    const clearPending = useCallback((rowKey: any, colName: string) => {
      const p = pendingFocusRef.current;
      if (!p) return;
      if (p.rowKey !== String(rowKey) || p.colName !== colName) return;
      pendingFocusRef.current = null;
    }, []);

    const isPendingCell = useCallback((rowKey: any, colName: string) => {
      const p = pendingFocusRef.current;
      return !!p && p.rowKey === String(rowKey) && p.colName === colName;
    }, []);

    const [changesState, setChangesState] = useState(initialChangesState);

    const getFirstCol = useCallback(
      () => fieldQuery.data?.tableColumns?.[0]?.name,
      [fieldQuery.data?.tableColumns],
    );

    const newRowInit = useCallback(
      (e: any) => {
        const data = e.data;

        if (!data.__id) data.__id = createId();

        fieldQuery.data?.tableColumns
          ?.filter((x) => x.dataType === DataType.Boolean)
          ?.forEach((x) => {
            data[x.name] = false;
          });

        const firstCol = getFirstCol();
        if (firstCol) {
          const key = String(data.__id);
          setPending(
            key,
            firstCol,
            tableColumns[firstCol]?.dataType === DataType.Dictionary,
          );

          setChangesState((old) => ({
            ...old,
            editRowKey: key,
            editColumnKey: firstCol,
          }));
        }
      },
      [fieldQuery.data?.tableColumns, getFirstCol, setPending],
    );

    const usedDictionaries = useDictionariesByIds(
      fieldQuery.data?.tableColumns
        ?.filter((c) => c.dictionaryId)
        .map((c) => c.dictionaryId!),
    );

    const referencingColumns: Record<string, DataBlockDictionaryColumnDto> =
      useMemo(() => {
        const values: typeof referencingColumns = {};
        usedDictionaries
          ?.filter((d) => !!d.columns.find((x) => x.referenceDictionaryId))
          .flatMap((d) => d.columns)
          .forEach((c) => {
            values[c.id] = c;
          });
        return values;
      }, [usedDictionaries]);

    const [referencingColumnsValues, setReferencingColumnsValues] = useState<
      Record<number, DataBlockDictionaryItemDto[]>
    >({});

    const [columnDictionaryValues, setColumnDictionaryValues] = useState<
      Record<string, DataBlockDictionaryItemDto[]>
    >({});

    const getPossibleValuesForColumn = useCallback(
      (
        column: DataBlockTableColumnDto,
        columnIndex: number,
        currentRowData: any,
      ) => {
        let possibleValues = columnDictionaryValues[column.name] ?? [];

        for (
          let previousColumnIndex = 0;
          previousColumnIndex < columnIndex;
          previousColumnIndex++
        ) {
          const previousColumnDefinition =
            fieldQuery.data?.tableColumns![previousColumnIndex];
          if (!previousColumnDefinition) continue;
          if (previousColumnDefinition.dictionaryId !== column.dictionaryId)
            // previous column is bound to another dictionary, do not use it to filter values for current column
            continue;
          //if (!previousColumnDefinition.dictionaryColumnName) continue;

          const previousColumnValue =
            currentRowData[previousColumnDefinition.name];
          if (previousColumnValue) {
            possibleValues = possibleValues.filter((x) =>
              previousColumnDefinition.dictionaryColumnId === null
                ? x?.id === previousColumnValue
                : x?.values?.[
                    previousColumnDefinition.dictionaryColumnName!
                  ] === previousColumnValue,
            );
          }
        }
        const uniqueValues = new Set(
          possibleValues.map((x) =>
            column.dictionaryColumnName
              ? (x.values?.[column.dictionaryColumnName] as string)
              : x.value,
          ),
        );

          const getDisplayValue = (value: unknown) => {
              if (column.dictionaryColumnId !== null) {
                  return (
                      referencingColumnsValues[column.dictionaryColumnId]?.find(
                          (x) => x.id == value,
                      )?.value ?? value
                  );
              }

              return value;
          };

        return Array.from(uniqueValues.values()).sort((a, b) =>
            String(getDisplayValue(a) ?? '').localeCompare(
                String(getDisplayValue(b) ?? ''),
            ),
        );
      },
        [
            columnDictionaryValues,
            fieldQuery.data?.tableColumns,
            referencingColumnsValues,
        ],
    );

    const columnCellValueChangedHandlers = useMemo(() => {
      const tableColumns = fieldQuery.data?.tableColumns;
      if (!tableColumns) return undefined;
      return tableColumns.map((editedColumn, editedColumnIndex) => {
        if (editedColumn.dataType === DataType.DateOnly) {
          return function handleDateOnlyColumnChange(
            newData: any,
            value: any,
            oldData: any,
          ) {
            if (!oldData[editedColumn.name] && value) {
              // fix for Bug 46841: "Date" field isn't displayed in the table correctly
              // for some reason, when setting the date for the first time the Timezone is different
              value = convertLocalDateToUTC(value);
            }
            newData[editedColumn.name] = value;
          };
        }

        if (editedColumn.dataType !== DataType.Dictionary) return undefined;

        return function handleMainDictionaryColumnChange(
          newData: any,
          value: any,
          oldData: any,
        ) {
          const currentValues = Object.assign(oldData, {});
          currentValues[editedColumn.name] = value;
          newData[editedColumn.name] = value;

          // When the value of Dictionary column changes, we search for other columns of the same Dictionary type in the table.
          // If such columns are found, and they are bound to some DictionaryColumn (not 'Value'), we change the value
          // according to selected DictionaryItem.

          tableColumns.forEach((column, index) => {
            // only process columns AFTER the current one
            if (index <= editedColumnIndex) return;
            // only process columns bound to the same Dictionary
            if (column.dictionaryId !== editedColumn.dictionaryId) return;
            if (!column.dictionaryColumnId || !column.dictionaryColumnName)
              return;

            const possibleValues = getPossibleValuesForColumn(
              column,
              index,
              currentValues,
            );
            if (possibleValues.length === 1) {
              newData[column.name] = possibleValues[0];
              currentValues[column.name] = possibleValues[0];
            } else {
              newData[column.name] = '';
              currentValues[column.name] = '';
            }
          });
        };
      });
    }, [
      fieldQuery.data?.tableColumns,
      columnDictionaryValues,
      getPossibleValuesForColumn,
    ]);
    const isEditing = !(isInDesigner || isOnlyView);
    const isTableLabelVisible = isTitleVisible && !!label;
    const isFloatingTableLabel = isTableLabelVisible && isEditing;
    const isEditingRef = useRef(isEditing);
    isEditingRef.current = isEditing;
    const [tableId] = useState(() => createId());

    const singleValueBooleanColumns = useMemo(() => {
      if (!fieldQuery.data) return [];
      if (!inputValueFromContext) return [];
      const singleValueBooleanColumns =
        fieldQuery.data.tableColumns?.filter(
          (x) => x.dataType === DataType.Boolean && x.isSingleValue,
        ) ?? [];
      if (singleValueBooleanColumns.length === 0) return [];
      return singleValueBooleanColumns;
    }, [fieldQuery.data]);

    const selectedRowIds = useMemo(
      function findRowsWithBooleanSingleValueFields() {
        return inputValueFromContext
          .filter(
            (row) =>
              !!singleValueBooleanColumns.find((column) => row[column.name]),
          )
          .map((row) => row.__id);
      },
      [inputValueFromContext, singleValueBooleanColumns],
    );
    const tableColumns = useMemo(() => {
      const result: Record<string, DataBlockTableColumnDto> = {};
      fieldQuery.data?.tableColumns?.forEach((column) => {
        result[column.name] = column;
      });
      return result;
    }, [fieldQuery.data?.tableColumns]);

    const setDataBlockFieldValue = useContextSelector(
      InputValuesContext,
      isShared ? (v) => v.setSharedValues : (v) => v.setValues,
    );

    const setValues = useCallback(
      (values: UIEditorDataObjectValue) =>
        setDataBlockFieldValue?.(dataKey, values),
      [setDataBlockFieldValue, dataKey],
    );

    const onChangeChanges = useEventCallback((changes: DataChange[]) => {
      for (const change of changes) {
        switch (change.type) {
          case 'insert': {
            const key = normalizeDxKey(
              (change as any).key ?? (change.data as any)?.__id,
            );

            const newRow = { __id: key, ...change.data };
            setValues([...inputValueFromContext, newRow]);

            const firstCol = getFirstCol();
            if (firstCol && !pendingFocusRef.current) {
              setPending(
                key,
                firstCol,
                tableColumns[firstCol]?.dataType === DataType.Dictionary,
              );
              setChangesState((old) => ({
                ...old,
                editRowKey: key,
                editColumnKey: firstCol,
              }));
            }
            break;
          }

          case 'update': {
            const key = normalizeDxKey((change as any).key);
            const old = inputValueFromContext.find((x) => x.__id == key);
            // preserve reference to stop input from losing focus
            const newObj = old ? Object.assign(old, change.data) : change.data;
            setValues(
              inputValueFromContext.map((x) => (x.__id == key ? newObj : x)),
            );
            break;
          }

          case 'remove': {
            const key = normalizeDxKey((change as any).key);
            setValues(inputValueFromContext.filter((x) => x.__id !== key));
            break;
          }
        }
      }

      if (!shallowEqual(changes, changesState.changes))
        setChangesState((old) => ({
          ...old,
          changes: changes.filter((x) => x.type === 'update'),
        }));
    });

    const setEditRowKeyCallback = useCallback(
      (v: string) =>
        setChangesState((old) => ({ ...old, editRowKey: normalizeDxKey(v) })),
      [],
    );

    const setEditColumnKeyCallback = useCallback(
      (v: string) => setChangesState((old) => ({ ...old, editColumnKey: v })),
      [],
    );

    const handleContentReady = useCallback((e: any) => {
      const inst: DevDataGrid | undefined = e.component;
      const p = pendingFocusRef.current;
      if (!p || !inst) return;

      const rowIndex = inst.getRowIndexByKey?.(p.rowKey);

      if (typeof rowIndex !== 'number' || rowIndex < 0) return;

      inst.editCell?.(rowIndex, p.colName);
      if (!p.keepUntilAutoFocus) {
        pendingFocusRef.current = null;
      }
    }, []);

    return (
      <div
        className={styles.container}
        style={{
          width: size,
        }}
        ref={(ref) => connect(drag(ref!))}
        data-test-id={`field-${props.dataKey}`}
        data-app-field={props.dataKey}
      >
        <div
          className={styles.tableBlockContainer}
          data-align-with-inputs={isAlignedWithInputs}
          data-floating-title={isFloatingTableLabel}
        >
          {isFloatingTableLabel && (
            <div className={styles.tableTitleOverlay}>{label}</div>
          )}
          {isTableLabelVisible && !isFloatingTableLabel && (
            <div className={styles.tableTitleStatic}>{label}</div>
          )}
          {(fieldQuery.data?.tableColumns ?? [])
            .filter((x) => x.dataType === DataType.Dictionary)
            .map((column) => (
              <DictionaryColumnDataProvider
                key={column.name}
                dictionaryId={column.dictionaryId}
                onValuesReceived={(data) => {
                  setColumnDictionaryValues((prev) => {
                    return {
                      ...prev,
                      [column.name]: data,
                    };
                  });
                }}
              />
            ))}
          {Object.keys(referencingColumns).map((id) => (
            <DictionaryColumnDataProvider
              key={referencingColumns[id].referenceDictionaryId}
              dictionaryId={referencingColumns[id].referenceDictionaryId}
              onValuesReceived={(data) =>
                setReferencingColumnsValues((prev) => ({
                  ...prev,
                  [referencingColumns[id].id]: data,
                }))
              }
            />
          ))}
          <DXG.DataGrid
            onContentReady={handleContentReady}
            ref={dataGridRef}
            key={tableKey}
            keyExpr={idField}
            onInitNewRow={newRowInit}
            dataSource={mappedValueFromContext}
            showBorders={true}
            repaintChangesOnly={false}
            selectedRowKeys={selectedRowIds}
            onEditorPreparing={(e) => {
              e.editorOptions['valueChangeEvent'] = 'keyup';
              if (
                e.dataField &&
                tableColumns[e.dataField]?.dataType === DataType.MultilineText
              )
                e.editorName = 'dxTextArea';
            }}
          >
            <DXG.Paging enabled={false} />
            {isEditing && (
              <DXG.Editing
                mode="cell"
                allowUpdating={true}
                allowAdding={true}
                allowDeleting={true}
                changes={changesState.changes}
                editRowKey={changesState.editRowKey}
                onEditRowKeyChange={setEditRowKeyCallback}
                editColumnName={changesState.editColumnKey}
                onEditColumnNameChange={setEditColumnKeyCallback}
                selectTextOnEditStart={true}
                startEditAction="click"
                onChangesChange={onChangeChanges}
              />
            )}
            {fieldQuery.data?.tableColumns?.map((column, index) => (
              <DXG.Column
                key={column.name}
                renderAsync={true}
                dataField={column.name}
                caption={i18nField.t(column.name, {
                  keyPrefix: columnLocalizationPrefix,
                  defaultValue: column.name,
                })}
                cssClass={
                  column.dataType === DataType.MultilineText
                    ? styles.multilineTextCell
                    : undefined
                }
                dataType={getDxDataType(column)}
                setCellValue={columnCellValueChangedHandlers?.[index]}
                cellRender={
                  column.dataType === DataType.Dictionary
                    ? column.dictionaryColumnId === null
                      ? (cell) => {
                          let value = cell.value;
                          if (!isNaN(value)) {
                            value =
                              columnDictionaryValues[column.name]?.find(
                                (x) => x.id === value,
                              )?.value ?? value;
                          }

                          return i18nField.t(value);
                        }
                      : (cell) => {
                          const value = referencingColumnsValues[
                            column.dictionaryColumnId!
                          ]
                            ? (referencingColumnsValues[
                                column.dictionaryColumnId!
                              ].find((x) => x.id == cell.value)?.value ??
                              cell.value)
                            : cell.value;
                          return i18nField.t(value);
                        }
                    : undefined
                }
                editCellRender={
                  column.dataType === DataType.DateTime ||
                  column.dataType === DataType.DateOnly
                    ? (cell) => {
                        return (
                          <DXG.DateBox
                            type={
                              column.dataType === DataType.DateTime
                                ? 'datetime'
                                : 'date'
                            }
                            name={`table_${tableId}_${column.name}_${cell.key}`}
                            value={cell.value}
                            onValueChanged={(e) => cell.setValue(e.value)}
                          />
                        );
                      }
                    : column.dataType === DataType.Boolean &&
                        column.isSingleValue
                      ? (cell) => {
                          return (
                            <RadioButton
                              name={`table_${tableId}_${column.name}_${cell.key}`}
                              checked={!!cell.value}
                              value={cell.key}
                              className={styles.radioButtonLeveler}
                              disabled={!isEditingRef.current}
                              onChange={(_) => {
                                const newData = inputValueFromContext.map(
                                  (row) => {
                                    const newRow = { ...row };
                                    newRow[column.name] = row.__id === cell.key;
                                    return newRow;
                                  },
                                );

                                sortBySingleValueBooleansFirst(
                                  singleValueBooleanColumns,
                                  newData,
                                  cell.key,
                                );

                                setValues(newData);
                              }}
                            />
                          );
                        }
                      : column.dataType === DataType.Dictionary
                        ? (cell) => {
                            const autoFocus = isPendingCell(
                              cell.key,
                              column.name,
                            );

                            if (column.dictionaryColumnId !== null) {
                              const options = getPossibleValuesForColumn(
                                column,
                                index,
                                cell.data,
                              );
                              return (
                                <StyledAutocomplete
                                  autoFocus={autoFocus}
                                  onAutoFocus={() => {
                                    clearPending(cell.key, column.name);
                                  }}
                                  options={options ?? []}
                                  value={cell.value}
                                  getOptionLabel={(value) => {
                                    const rawValue = referencingColumnsValues[
                                      column.dictionaryColumnId!
                                    ]
                                      ? (referencingColumnsValues[
                                          column.dictionaryColumnId!
                                        ].find((x) => x.id == value)?.value ??
                                        value)
                                      : value;
                                    return i18nField.t(rawValue);
                                  }}
                                  freeSolo={column.isOptionAllowed}
                                  placeholder={''}
                                  onInput={(e: any) => {
                                    const v = e.target?.value;
                                    cell.setValue(v);
                                  }}
                                  onChange={(_, value) => {
                                    cell.setValue(value);
                                    dataGridRef.current
                                      ?.instance()
                                      .closeEditCell();

                                    setTimeout(() => {
                                      // repaint is needed for #54752 (when cell allows custom value, user enters custom value and clicks outside the Combobox)
                                      dataGridRef.current?.instance().repaint();
                                    });
                                  }}
                                />
                              );
                            } else {
                              let value = cell.value;
                              if (!isNaN(value)) {
                                value =
                                  columnDictionaryValues[column.name].find(
                                    (x) => x.id === value,
                                  ) ?? value;
                              }
                              return (
                                <StyledAutocomplete
                                  autoFocus={autoFocus}
                                  onAutoFocus={() => {
                                    clearPending(cell.key, column.name);
                                  }}
                                  options={
                                    columnDictionaryValues[column.name] ??
                                    emptyArray<DataBlockDictionaryItemDto>()
                                  }
                                  getOptionLabel={'value'}
                                  idFunction={'id'}
                                  value={value}
                                  freeSolo={column.isOptionAllowed}
                                  placeholder={''}
                                  onInput={(e: any) => {
                                    const val = e.target?.value;
                                    cell.setValue(val);
                                  }}
                                  onChange={(_, value) => {
                                    if (
                                      typeof value === 'object' &&
                                      value !== null
                                    ) {
                                      cell.setValue(value.id);
                                    } else {
                                      cell.setValue(value);
                                    }

                                    dataGridRef.current
                                      ?.instance()
                                      .closeEditCell();

                                    setTimeout(() => {
                                      // repaint is needed for #54752 (when cell allows custom value, user enters custom value and clicks outside the Combobox)
                                      dataGridRef.current?.instance().repaint();
                                    });
                                  }}
                                />
                              );
                            }
                          }
                        : undefined
                }
              ></DXG.Column>
            ))}
          </DXG.DataGrid>
        </div>
      </div>
    );
  });

export const DictionaryColumnDataProvider: React.FC<{
  dictionaryId: number | null;
  onValuesReceived: (data: DataBlockDictionaryItemDto[]) => void;
}> = (props) => {
  const { dictionaryId } = props;
  const i18n = useTranslation(NS_DataBlockFields);

  const dictionaryItemsQuery =
    QueryFactory.DataBlockDictionaryItemQuery.useGetQuery(
      { dictionaryId: dictionaryId ?? 0, all: true },
      {
        enabled: !!dictionaryId,
      },
    );

  useEffect(
    function callOnValuesReceivedWhenDataIsReceived() {
      if (dictionaryItemsQuery.data) {
        props.onValuesReceived(
          dictionaryItemsQuery.data.data
            .filter((x) => !x.isDeleted)
            .map((x) => ({
              id: x.id,
              value: i18n.t(x.value as any, { nsSeparator: false }),
              isDeleted: false,
              values: x.values,
              dictionaryId: x.dictionaryId,
              manualOrderPosition: x.manualOrderPosition,
            })),
        );
      }
    },
    [dictionaryItemsQuery.data],
  );

  return null;
};

export function getDxDataType(column: {
  dataType: DataType;
  dictionaryColumnId: number | null;
}): 'string' | 'number' | 'date' | 'boolean' | 'object' | 'datetime' {
  const dataType = column.dataType;

  switch (dataType) {
    case DataType.Text:
    case DataType.MultilineText:
      return 'string';

    case DataType.Number:
      return 'number';
    case DataType.Boolean:
      return 'boolean';
    case DataType.DateTime:
      return 'datetime';
    case DataType.DateOnly:
      return 'date';
    case DataType.Dictionary:
      // for other columns (except Value) we store values as text for Dictionary columns
      return 'string';
    //return column.dictionaryColumnId === null ? 'number' : 'string';

    case DataType.TimeOnly:
    case DataType.Table:
    case DataType.Undefined:
      throw new Error(`Type '${dataType}' is not supported as Table column`);

    default:
      assertNever(dataType);
  }
}

function sortBySingleValueBooleansFirst(
  singleValueBooleanColumns: QueryFactory.DataBlockTableColumnDto[],
  values: Row[],
  priorityRowId: string,
) {
  values.sort((row1, row2) => {
    const isRow1Selected = !!singleValueBooleanColumns.find(
      (column) => row1[column.name],
    );
    const isRow2Selected = !!singleValueBooleanColumns.find(
      (column) => row2[column.name],
    );

    if (isRow1Selected === isRow2Selected) {
      if (row1.__id === priorityRowId) return -1;
      if (row2.__id === priorityRowId) return 1;

      return 0;
    }
    return isRow1Selected ? -1 : 1;
  });
}

const TableSettings = () => {
  const { actions, props } = useNode((node) => ({
    props: node.data.props as TableProps,
  }));
  const { setProp } = actions;

  return (
    <div className={styles.settings}>
      <WidthSelectControl props={props} setProp={setProp} />
    </div>
  );
};

export const TableDefaultProps: DefaultProps = {
  size: UIEditorBlockWidth.Stretch,
  isEditable: false,
  isLabel: false,
  label: '',
  isShared: false,
  isTitleVisible: false,
  isAlignedWithInputs: false,
};

Table.craft = {
  defaultProps: TableDefaultProps,
  related: {
    settings: TableSettings,
  },
};
